import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, FileText, Calendar, DollarSign, MapPin, Users, Clock, Check, Loader, Shield } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PublicAgreementApproval = () => {
  const { token } = useParams();
  const [agreement, setAgreement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [alreadyApproved, setAlreadyApproved] = useState(false);

  useEffect(() => {
    fetchAgreement();
  }, [token]);

  const fetchAgreement = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/public/agreement/${token}`);
      
      if (response.data.already_approved) {
        setAlreadyApproved(true);
        setAgreement(response.data);
      } else {
        setAgreement(response.data);
        setCustomerName(response.data.lead_name || '');
        setCustomerEmail(response.data.lead_email || '');
      }
      setLoading(false);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load agreement. The link may be invalid or expired.');
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!customerName.trim()) {
      setError('Please enter your name');
      return;
    }
    if (!customerEmail.trim() || !customerEmail.includes('@')) {
      setError('Please enter a valid email address');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      await axios.post(`${BACKEND_URL}/api/public/agreement/${token}/approve`, {
        customer_name: customerName,
        customer_email: customerEmail
      });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to sign agreement. Please try again.');
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <Loader className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading agreement...</p>
        </div>
      </div>
    );
  }

  if (error && !agreement) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Shield className="w-8 h-8 text-red-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Agreement Not Found</h1>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-blue-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-12 h-12 text-green-600" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Agreement Signed!</h1>
          <p className="text-lg text-gray-700 mb-6">
            You have successfully signed agreement <span className="font-semibold text-blue-600">{agreement?.agreement_number}</span>
          </p>
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
            <p className="text-green-800 font-medium mb-2">🚀 Your Journey Begins Now!</p>
            <p className="text-green-700 text-sm">
              We'll start processing your application immediately. Our team will keep you updated at every step of the way.
            </p>
          </div>
          <p className="text-gray-500 text-sm">
            A confirmation email has been sent to <span className="font-medium">{customerEmail}</span>
          </p>
        </div>
      </div>
    );
  }

  if (alreadyApproved) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-blue-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Already Signed</h1>
          <p className="text-gray-600">{agreement?.message}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-xl mb-6 overflow-hidden">
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-8">
            <div className="flex items-center gap-3 mb-2">
              <Shield className="w-8 h-8" />
              <h1 className="text-3xl font-bold">Service Agreement</h1>
            </div>
            <p className="text-blue-100">Please review the agreement carefully and sign if you agree to the terms</p>
          </div>
          
          {/* Agreement Info Bar */}
          <div className="bg-gray-50 px-8 py-4 border-b grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-500">Agreement Number</p>
              <p className="font-semibold text-gray-900">{agreement.agreement_number}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Issue Date</p>
              <p className="font-semibold text-gray-900">{new Date(agreement.issue_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
            </div>
          </div>
        </div>

        {/* Customer Details */}
        <div className="bg-white rounded-2xl shadow-xl p-8 mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Users className="w-6 h-6 text-blue-600" />
            Client Details
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-500">Name</p>
              <p className="font-medium text-gray-900">{agreement.lead_name}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Email</p>
              <p className="font-medium text-gray-900">{agreement.lead_email}</p>
            </div>
            {agreement.service_type && (
              <div>
                <p className="text-sm text-gray-500">Service Type</p>
                <p className="font-medium text-gray-900">{agreement.service_type}</p>
              </div>
            )}
            {agreement.destination_country && (
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-500">Destination</p>
                  <p className="font-medium text-gray-900">{agreement.destination_country}</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Service Details */}
        <div className="bg-white rounded-2xl shadow-xl p-8 mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <FileText className="w-6 h-6 text-blue-600" />
            Services Included
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-gray-200">
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Service</th>
                  <th className="text-center py-3 px-4 text-sm font-semibold text-gray-700">Qty</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Rate</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Amount</th>
                </tr>
              </thead>
              <tbody>
                {agreement.line_items?.map((item, index) => (
                  <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-4 px-4">
                      <p className="font-medium text-gray-900">{item.service_name}</p>
                      {item.description && (
                        <p className="text-sm text-gray-500 mt-1">{item.description}</p>
                      )}
                      {item.deliverables && item.deliverables.length > 0 && (
                        <ul className="mt-2 space-y-1">
                          {item.deliverables.map((deliverable, idx) => (
                            <li key={idx} className="text-xs text-gray-600 flex items-start gap-1">
                              <Check className="w-3 h-3 text-green-600 mt-0.5 flex-shrink-0" />
                              {deliverable}
                            </li>
                          ))}
                        </ul>
                      )}
                    </td>
                    <td className="text-center py-4 px-4 text-gray-700">{item.quantity}</td>
                    <td className="text-right py-4 px-4 text-gray-700">₹{parseFloat(item.rate).toLocaleString('en-IN')}</td>
                    <td className="text-right py-4 px-4 font-medium text-gray-900">₹{parseFloat(item.amount).toLocaleString('en-IN')}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="border-t-2 border-gray-300">
                <tr>
                  <td colSpan="3" className="py-3 px-4 text-right font-semibold text-gray-700">Subtotal</td>
                  <td className="py-3 px-4 text-right font-semibold text-gray-900">₹{parseFloat(agreement.subtotal).toLocaleString('en-IN')}</td>
                </tr>
                {agreement.discount_percentage > 0 && (
                  <tr>
                    <td colSpan="3" className="py-2 px-4 text-right text-gray-600">
                      Discount ({agreement.discount_percentage}%)
                    </td>
                    <td className="py-2 px-4 text-right text-green-600">-₹{parseFloat(agreement.discount_amount || 0).toLocaleString('en-IN')}</td>
                  </tr>
                )}
                <tr className="bg-blue-50">
                  <td colSpan="3" className="py-4 px-4 text-right text-lg font-bold text-gray-900">Total Amount</td>
                  <td className="py-4 px-4 text-right text-lg font-bold text-blue-600">₹{parseFloat(agreement.total_amount).toLocaleString('en-IN')}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        {/* Agreement Terms */}
        {agreement.agreement_terms && (
          <div className="bg-white rounded-2xl shadow-xl p-8 mb-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Agreement Terms & Conditions</h2>
            <div className="prose prose-sm max-w-none text-gray-700 whitespace-pre-wrap bg-gray-50 p-6 rounded-lg border border-gray-200">
              {agreement.agreement_terms}
            </div>
          </div>
        )}

        {/* Validity */}
        {agreement.validity_date && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
            <div className="flex items-center gap-2 text-amber-800">
              <Clock className="w-5 h-5" />
              <p className="font-medium">
                This agreement is valid until {new Date(agreement.validity_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}
              </p>
            </div>
          </div>
        )}

        {/* Digital Signature Section */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <h2 className="text-xl font-bold text-gray-900 mb-6">Digital Signature</h2>
          
          <div className="space-y-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Your Full Name *</label>
              <input
                type="text"
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter your full legal name"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Your Email Address *</label>
              <input
                type="email"
                value={customerEmail}
                onChange={(e) => setCustomerEmail(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter your email address"
              />
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <p className="text-blue-800 text-sm">
              <strong className="font-semibold">Important:</strong> By signing this agreement, you acknowledge that you have read, understood, and agree to be bound by all terms and conditions stated above. Your digital signature has the same legal validity as a physical signature.
            </p>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
              {error}
            </div>
          )}

          <button
            onClick={handleApprove}
            disabled={submitting}
            className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold py-4 rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-lg"
          >
            {submitting ? (
              <>
                <Loader className="w-5 h-5 animate-spin" />
                Processing Signature...
              </>
            ) : (
              <>
                <Shield className="w-5 h-5" />
                I Agree - Sign Agreement
              </>
            )}
          </button>

          <p className="text-xs text-gray-500 text-center mt-4">
            Digital signature timestamp and IP address will be recorded for legal purposes.
          </p>
        </div>

        {/* Footer */}
        <div className="text-center mt-8 text-gray-500 text-sm">
          <p>Powered by Visaasure CRM</p>
        </div>
      </div>
    </div>
  );
};

export default PublicAgreementApproval;
